
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Button,
  Card,
  CardHeader,
  CardBody,
  Heading,
  Text,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  Select,
  Checkbox,
  NumberInput,
  NumberInputField,
  SimpleGrid,
} from '@chakra-ui/react'
import { Save } from 'lucide-react'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { BackupService } from '@/lib/services/backupService'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { useThemeColors } from '@/hooks/useThemeColors'
import type { CreateBackupData } from '@/types/backup'

export default function CreateBackupPage() {
  const colors = useThemeColors()
  const navigate = useNavigate()
  const { showError, showSuccess } = useToastNotification()

  const [isLoading, setIsLoading] = useState(false)
  const [tables, setTables] = useState<string[]>([])
  const [formData, setFormData] = useState<CreateBackupData>({
    name: '',
    type: 'full',
    compression: 'gzip',
    encrypted: false,
    storage_disk: 'local',
    retention_days: 30,
  })

  useEffect(() => {
    const fetchTables = async () => {
      try {
        const tablesData = await BackupService.getBackupTables()
        setTables(tablesData)
      } catch (error: any) {
        showError(error)
      }
    }
    fetchTables()
  }, [showError])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      await BackupService.createBackup(formData)
      showSuccess('Backup created successfully')
      navigate('/system-settings/backup-restore')
    } catch (error: any) {
      showError(error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleChange = (field: keyof CreateBackupData, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  return (
    <AdminLayout>
      <Box p={4} h="full" overflow="hidden" display="flex" flexDirection="column">
        <Card h="full" display="flex" flexDirection="column">
          <CardHeader flexShrink={0}>
            <Heading size="md">Create Backup</Heading>
            <Text fontSize="sm" color={colors.text} mt={1}>
              Create a new system backup
            </Text>
          </CardHeader>
          <CardBody overflowY="auto" flex="1" minH={0}>
            <form onSubmit={handleSubmit}>
                <VStack spacing={6} align="stretch">
                  <FormControl isRequired>
                    <FormLabel>Backup Name</FormLabel>
                    <Input
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      placeholder="Enter backup name"
                    />
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel>Backup Type</FormLabel>
                    <Select
                      value={formData.type}
                      onChange={(e) => handleChange('type', e.target.value as any)}
                    >
                      <option value="database">Database Only</option>
                      <option value="files">Files Only</option>
                      <option value="full">Full Backup (Database + Files)</option>
                    </Select>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Compression</FormLabel>
                    <Select
                      value={formData.compression}
                      onChange={(e) => handleChange('compression', e.target.value as any)}
                    >
                      <option value="none">None</option>
                      <option value="gzip">Gzip</option>
                      <option value="zip">Zip</option>
                    </Select>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Storage Disk</FormLabel>
                    <Select
                      value={formData.storage_disk}
                      onChange={(e) => handleChange('storage_disk', e.target.value as any)}
                    >
                      <option value="local">Local Storage</option>
                      <option value="s3">S3 Storage</option>
                    </Select>
                  </FormControl>

                  <FormControl>
                    <Checkbox
                      isChecked={formData.encrypted}
                      onChange={(e) => handleChange('encrypted', e.target.checked)}
                    >
                      Encrypt Backup
                    </Checkbox>
                  </FormControl>

                  {(formData.type === 'database' || formData.type === 'full') && (
                    <FormControl>
                      <HStack justify="space-between" mb={2}>
                        <FormLabel mb={0}>Select Tables (leave empty for all)</FormLabel>
                        {tables.length > 0 && (
                          <HStack spacing={2}>
                            <Text fontSize="sm" color={colors.textMuted}>
                              {formData.tables_included?.length || 0} of {tables.length} selected
                            </Text>
                            <Button
                              size="xs"
                              variant="outline"
                              onClick={() => {
                                const allSelected = formData.tables_included?.length === tables.length
                                handleChange('tables_included', allSelected ? undefined : tables)
                              }}
                            >
                              {formData.tables_included?.length === tables.length ? 'Uncheck All' : 'Check All'}
                            </Button>
                          </HStack>
                        )}
                      </HStack>
                      {tables.length > 0 ? (
                        <Box
                          border="1px"
                          borderColor={colors.border}
                          borderRadius="md"
                          p={4}
                          maxH="300px"
                          overflowY="auto"
                          bg={colors.sectionBg}
                        >
                          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={3}>
                            {tables.map((table) => (
                              <Checkbox
                                key={table}
                                isChecked={formData.tables_included?.includes(table) || false}
                                onChange={(e) => {
                                  const current = formData.tables_included || []
                                  const updated = e.target.checked
                                    ? [...current, table]
                                    : current.filter((t) => t !== table)
                                  handleChange('tables_included', updated.length > 0 ? updated : undefined)
                                }}
                              >
                                {table}
                              </Checkbox>
                            ))}
                          </SimpleGrid>
                        </Box>
                      ) : (
                        <Box p={4} border="1px" borderColor={colors.border} borderRadius="md" bg={colors.sectionBg}>
                          <Text fontSize="sm" color={colors.textMuted}>
                            Loading tables...
                          </Text>
                        </Box>
                      )}
                      <Box mt={2} fontSize="xs" color={colors.textMuted}>
                        {formData.tables_included && formData.tables_included.length > 0
                          ? `Selected: ${formData.tables_included.join(', ')}`
                          : 'No tables selected - all tables will be backed up'}
                      </Box>
                    </FormControl>
                  )}

                  <FormControl>
                    <FormLabel>Retention Days (optional)</FormLabel>
                    <NumberInput
                      value={formData.retention_days}
                      onChange={(_, value) => handleChange('retention_days', value || undefined)}
                      min={1}
                    >
                      <NumberInputField placeholder="30" />
                    </NumberInput>
                  </FormControl>

                  <HStack justify="flex-end">
                    <Button
                      variant="ghost"
                      onClick={() => navigate('/system-settings/backup-restore')}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      colorScheme="blue"
                      leftIcon={<Save size={18} />}
                      isLoading={isLoading}
                      loadingText="Creating..."
                    >
                      Create Backup
                    </Button>
                  </HStack>
                </VStack>
              </form>
            </CardBody>
          </Card>
        </Box>
      </AdminLayout>
  )
}

